import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { MinusIcon, SearchIcon, XIcon, BackArrowIcon, ForwardArrowIcon } from './icons'
import { Button, Text } from 'ui'
import { CheckBox } from './FilterModal'
import type { User } from 'utils/models'
import { ModalHeader, CenteredModal, SearchInput, FlexSpaceBetween, ModalButtonContainer, SearchContainer, PepoleDisplayContainer, SelectedMembersContainer, SearchListContainer, IndicationLetterContainer, ListItem, PersonDisplayContainer, BlueBubbleWithShortNameContainer, MinusIconContainer, CheckBoxContainer } from './SharedStyledComponents'
import { ShareModalProps, GroupedContacts } from './types'
import { formatPhoneNumber, groupContactsByLetter, handlePersonCarouselScroll, getVisibleContacts } from './function'
import { useCoupons } from 'store/CouponsContext'
import { useCouponsAppData } from 'store/CouponsAppDataContext'

const Container = styled(CenteredModal)`
  height: 714px;
  padding: 24px 16px 48px 16px;
  @media (max-width: 1081px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    transform: none;
    border-radius: 0;
    padding: 16px;
    background: #F6F6F6;
    max-height: 100vh;
    z-index: 9999;
  }
`

const ResponsiveSearchListContainer = styled(SearchListContainer)`
  @media (max-width: 1081px) {
    max-height: none;
    flex: 1;
    padding-bottom: 100px;
  }
`



const ItemNameAndPhone = FlexSpaceBetween

const StyledShareButton = styled(Button)`
  background-color: #007EFF !important;
  box-shadow: 0 !important;
  padding: 16px !important;
  width: 212px !important;
  @media (max-width: 1081px) {
    width: 100% !important;
  }
`

const DesktopButtonContainer = styled(ModalButtonContainer)`
   @media (max-width: 1081px) {
    display: none;
  }
`

const numberOfPersons = 8

const ShareModal: React.FC<ShareModalProps> = ({
    onClose,
    checkedCouponsCount,
    onShare,
    selectedContacts,
    onContactSelect,
    searchQuery,
    onSearchChange,
    isSharing
}) => {
    const appData = useCouponsAppData()
    const [contacts, setContacts] = useState<User[]>([])
    const [groupedContacts, setGroupedContacts] = useState<GroupedContacts>({})
    const [scrollPosition, setScrollPosition] = useState(0)

    useEffect(() => {
        if (appData.orgMembersSorted) {
            setContacts(appData.orgMembersSorted)
        }
    }, [appData.orgMembersSorted])

    useEffect(() => {
        const grouped = groupContactsByLetter(contacts, searchQuery)
        setGroupedContacts(grouped)
    }, [contacts, searchQuery])

    const handleScrollLeft = () => {
        setScrollPosition(prev =>
            handlePersonCarouselScroll('left', prev, selectedContacts.size, numberOfPersons)
        )
    }

    const handleScrollRight = () => {
        setScrollPosition(prev =>
            handlePersonCarouselScroll('right', prev, selectedContacts.size, numberOfPersons)
        )
    }

    const shouldShowArrows = selectedContacts.size > numberOfPersons
    const visibleContacts = getVisibleContacts(selectedContacts, scrollPosition, numberOfPersons)

    return (
        <Container>
            <ModalHeader>
                <Text size="lg" weight="bold">שיתוף עם חברים</Text>
                <div style={{ cursor: 'pointer' }} onClick={onClose}>
                    <XIcon />
                </div>
            </ModalHeader>
            <SearchContainer>
                <SearchIcon />
                <SearchInput
                    placeholder="כולם פה, עם מי לשתף?"
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                />
            </SearchContainer>
            {selectedContacts.size > 0 && (
                <PepoleDisplayContainer>
                    {shouldShowArrows && (
                        <div style={{ cursor: 'pointer' }} onClick={handleScrollLeft}>
                            <BackArrowIcon fill='#272727' />
                        </div>
                    )}

                    {visibleContacts.map(userId => {
                        const contact = contacts.find(c => c.user_id === userId)
                        if (!contact) return null
                        return (
                            <PersonDisplayContainer key={userId}>
                                <BlueBubbleWithShortNameContainer>
                                    <MinusIconContainer onClick={() => onContactSelect(userId)}>
                                        <MinusIcon />
                                    </MinusIconContainer>
                                    <Text size="xxl" weight="bold" style={{ color: '#0692FF' }}>
                                        {contact.given_name?.charAt(0) || 'א'}
                                    </Text>
                                </BlueBubbleWithShortNameContainer>
                                <Text size="sm" weight="bold">{contact.given_name}</Text>
                            </PersonDisplayContainer>
                        )
                    })}

                    {shouldShowArrows && (
                        <div style={{ cursor: 'pointer' }} onClick={handleScrollRight}>
                            <ForwardArrowIcon fill='#272727' />
                        </div>
                    )}
                </PepoleDisplayContainer>
            )}
            <SelectedMembersContainer>
                <Text size="sm" weight="normal" style={{ color: '#888888' }}>
                    נבחרו {selectedContacts.size} חברי קהילה
                </Text>
            </SelectedMembersContainer>
            <ResponsiveSearchListContainer>
                {Object.entries(groupedContacts)
                    .sort(([a], [b]) => a.localeCompare(b, 'he'))
                    .map(([letter, contacts]) => (
                        <div key={letter}>
                            <IndicationLetterContainer>
                                <Text size="sm" weight="bold">{letter}</Text>
                            </IndicationLetterContainer>
                            {contacts.map(contact => (
                                <ListItem key={contact.user_id}>
                                    <CheckBoxContainer>
                                        <CheckBox
                                            type="checkbox"
                                            checked={selectedContacts.has(contact.user_id)}
                                            onChange={() => onContactSelect(contact.user_id)}
                                        />
                                    </CheckBoxContainer>
                                    <ItemNameAndPhone>
                                        <Text size="sm" weight="bold">
                                            {contact.given_name} {contact.family_name}
                                        </Text>
                                        <Text size="sm" weight="normal" style={{ color: '#888888', marginLeft: '10px' }}>
                                            {contact.phones?.[0] ? formatPhoneNumber(contact.phones[0]) : 'אין טלפון'}
                                        </Text>
                                    </ItemNameAndPhone>
                                </ListItem>
                            ))}
                        </div>
                    ))
                }
            </ResponsiveSearchListContainer>
            <DesktopButtonContainer isFixed={true}>
                <StyledShareButton onClick={onShare} disabled={isSharing || selectedContacts.size === 0}>
                    <Text size="md" weight="bold" textcolor='white'>
                        {isSharing ? 'שולח...' : `שיתוף ${checkedCouponsCount} הטבות עם חברים`}
                    </Text>
                </StyledShareButton>
            </DesktopButtonContainer>
        </Container >
    )
}

export default ShareModal